import { useHookstate } from "@hookstate/core";
import { useState, useEffect } from "react";
import { useP2PSettings } from "./useP2PSettings";
import * as Sentry from "@sentry/react";
import { readerDataStore } from "../../../hookstate/readerDataStore";
import { readerSettingsStore } from "../../../hookstate/readerSettingsStore";

// Upper bound for how long the reader is allowed to show a loading state
// before we surface the error UI and report a Sentry event. Long enough to
// absorb slow mobile networks and large releases (some have hundreds of
// pages) without false-alarming.
const READER_LOAD_TIMEOUT_MS = 60000;

export function useReaderLoaded() {
  const loaded = useHookstate(readerDataStore.loaded).get();
  const responseError = useHookstate(readerDataStore.responseError).get();
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (loaded || responseError) {
      return;
    }
    const timeout = setTimeout(() => {
      // Re-read store state at fire time — the query may have resolved or
      // errored in the short window between setTimeout firing and React
      // flushing the triggering state update.
      const latestLoaded = readerDataStore.loaded.get({ noproxy: true });
      const latestResponseError = readerDataStore.responseError.get({
        noproxy: true,
      });
      if (latestLoaded || latestResponseError) {
        return;
      }
      try {
        const releaseData = readerDataStore.releaseData.get({ noproxy: true });
        Sentry.captureException(new Error("Reader failed to load"), {
          extra: {
            ...window.location,
            // Distinguishes "release fetch never arrived" from "release
            // arrived but fragments didn't" — the real diagnostic split.
            releaseUuid: releaseData?.key,
            stage: releaseData ? "fragments" : "release",
          },
        });
      } catch {
        // Sentry may be blocked by ad blockers
      }
      setTimedOut(true);
    }, READER_LOAD_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [loaded, responseError]);

  const { p2pEnabled } = useP2PSettings();
  const releaseData = useHookstate(readerDataStore.releaseData).get({
    noproxy: true,
  });
  useEffect(() => {
    if (releaseData) {
      if (!releaseData?.can_edit && !releaseData?.is_augmented) {
        readerSettingsStore.p2pEnabled.set(false);
      }
    }
  }, [p2pEnabled, releaseData]);

  return { loaded, timedOut };
}
